import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { ElaborazioneResponse } from '@/types/api-responses.ts';

const POLLING_INTERVAL = 2000;

const NOMI_MESE = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const CASSE_LABEL: Record<string, string> = { CE: 'CE - Cassa Edile', EC: 'EC - Edilcassa' };

type UploadRuolo = 'archivio' | 'iscritti_fillea' | 'iscritti_feneal';

const CASSA_ARCHIVIO_EXT: Record<string, string> = { CE: '.rar', EC: '.zip' };

/** Prefissi file zip obbligatori per EC (Edilcassa) */
const EC_REQUIRED_PREFIXES = ['lavorato', 'imprese', 'iscritti_filca'];

const STEP_CONFIG: Record<number, { ruolo: UploadRuolo; container: string; accept: string }> = {
  2: { ruolo: 'archivio', container: 'archivio', accept: '' }, // accept dinamico da cassa
  3: { ruolo: 'iscritti_fillea', container: 'fillea', accept: '.xlsx,.xls,.csv' },
  4: { ruolo: 'iscritti_feneal', container: 'feneal', accept: '.xlsx,.xls,.csv' },
};

export class Elaborazioni extends AppComponent {
  static override componentName = 'wc-elaborazioni';

  protected override get templateId() { return 'elaborazioni'; }

  private pollingTimers = new Map<number, ReturnType<typeof setInterval>>();
  private elaborazioni: ElaborazioneResponse[] = [];
  private detailCache = new Map<number, ElaborazioneResponse>();
  private feniceEnabled = false;
  private feniceStaging = false;

  private currentStep = 1;
  private uploadArchivio = false;
  private uploadFillea = false;
  private uploadFeneal = false;
  private nomeFileArchivio = '';
  private nomeFileFillea = '';
  private nomeFileFeneal = '';

  protected override afterRender(): void {
    this.feniceEnabled = this.hasAttribute('data-fenice-enabled');
    this.feniceStaging = this.hasAttribute('data-fenice-staging');
    this.initDefaults();
    this.setupActions();
    this.caricaStorico();
  }

  private field(name: string): string {
    const radio = this.$(`[data-field="${name}"]:checked`) as HTMLInputElement | null;
    if (radio) return radio.value;
    return (this.$(`[data-field="${name}"]`) as HTMLInputElement | null)?.value ?? '';
  }

  private buildUploadUrl(ruolo: UploadRuolo): string {
    const cassa = this.field('cassa') || 'CE';
    const anno = this.field('anno');
    const mese = this.field('mese');
    return `/api/elaborazioni/upload?cassa=${cassa}&anno=${anno}&mese=${mese}&ruolo=${ruolo}`;
  }

  private initDefaults(): void {
    const now = new Date();
    const anno = this.$('[data-field="anno"]') as HTMLInputElement | null;
    const mese = this.$('[data-field="mese"]') as HTMLInputElement | null;
    if (anno) anno.value = String(now.getFullYear());
    if (mese) mese.value = String(now.getMonth() + 1);
    const ceRadio = this.$('[data-field="cassa"][value="CE"]') as HTMLInputElement | null;
    if (ceRadio) ceRadio.checked = true;
  }

  private getUploadFlag(ruolo: UploadRuolo): boolean {
    if (ruolo === 'archivio') return this.uploadArchivio;
    if (ruolo === 'iscritti_fillea') return this.uploadFillea;
    return this.uploadFeneal;
  }

  private setUploadFlag(ruolo: UploadRuolo, value: boolean): void {
    if (ruolo === 'archivio') this.uploadArchivio = value;
    else if (ruolo === 'iscritti_fillea') this.uploadFillea = value;
    else this.uploadFeneal = value;
  }

  private setNomeFile(ruolo: UploadRuolo, name: string): void {
    if (ruolo === 'archivio') this.nomeFileArchivio = name;
    else if (ruolo === 'iscritti_fillea') this.nomeFileFillea = name;
    else this.nomeFileFeneal = name;
  }

  private isMultiFileStep(step: number): boolean {
    return step === 2 && this.field('cassa') === 'EC';
  }

  private createDropzone(step: number): void {
    const config = STEP_CONFIG[step];
    if (!config) return;
    const container = this.$(`[data-dropzone-container="${config.container}"]`);
    if (!container) return;
    const url = this.buildUploadUrl(config.ruolo);
    const isMulti = this.isMultiFileStep(step);
    const accept = step === 2
      ? (CASSA_ARCHIVIO_EXT[this.field('cassa') || 'CE'] ?? '.rar,.zip')
      : config.accept;
    const maxFiles = isMulti ? 10 : 1;
    const send = isMulti ? 'queue' : 'auto';
    const showUploadBtn = isMulti ? false : true;
    templateParseToElement('elaborazioni-dropzone', container, { url, accept, maxFiles, send, showUploadBtn });
    this.setupDropzoneEvents(step);
  }

  private setupDropzoneEvents(step: number): void {
    const config = STEP_CONFIG[step];
    if (!config) return;
    const container = this.$(`[data-dropzone-container="${config.container}"]`);
    const el = container?.querySelector('[data-dropzone]') as HTMLElement | null;
    if (!el) return;

    if (this.isMultiFileStep(step)) {
      this.setupMultiFileEvents(el, step, config.ruolo);
    } else {
      this.setupSingleFileEvents(el, step, config.ruolo);
    }
  }

  /** Singolo file (CE archivio, FILLEA, FENEAL): upload auto, abilita "Avanti" al successo */
  private setupSingleFileEvents(el: HTMLElement, step: number, ruolo: UploadRuolo): void {
    el.addEventListener('uploaded.wx.dropzone', (e: Event) => {
      this.setUploadFlag(ruolo, true);
      const detail = (e as CustomEvent).detail;
      if (detail?.response?.filename) this.setNomeFile(ruolo, detail.response.filename);
      this.updateStepAvantiBtn(step, true);
    });

    el.addEventListener('error.wx.dropzone', () => {
      this.setUploadFlag(ruolo, false);
      this.updateStepAvantiBtn(step, false);
    });
  }

  /**
   * Multi-file (EC archivio): l'utente aggiunge N zip, il componente verifica
   * che ci siano i 3 file necessari e avvia l'upload (queue sequenziale).
   */
  private setupMultiFileEvents(el: HTMLElement, step: number, ruolo: UploadRuolo): void {
    const hintEl = this.$('[data-step2-hint]') as HTMLElement | null;

    el.addEventListener('added.wx.dropzone', () => {
      const instance = Weext.Dropzone.getInstance(el);
      if (!instance) return;

      const names = instance.queuedFiles.map(f => f.file.name.toLowerCase());
      const found = EC_REQUIRED_PREFIXES.filter(prefix =>
        names.some(n => n.startsWith(prefix))
      );
      const missing = EC_REQUIRED_PREFIXES.filter(prefix =>
        !names.some(n => n.startsWith(prefix))
      );

      if (hintEl) {
        if (missing.length === 0) {
          hintEl.textContent = 'Tutti i file necessari trovati. ';
          hintEl.className = 'small text-success mb-2';
        } else {
          hintEl.textContent = `Trovati: ${found.join(', ') || 'nessuno'}. Mancano: ${missing.join(', ')}`;
          hintEl.className = 'small text-warning mb-2';
        }
      }

      if (missing.length === 0 && !instance.isUploading) {
        queueMicrotask(() => instance.upload());
      }
    });

    el.addEventListener('complete.wx.dropzone', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.success > 0) {
        this.setUploadFlag(ruolo, true);
        this.nomeFileArchivio = `${detail.success} file .zip caricati`;
        this.updateStepAvantiBtn(step, true);
  
      } else {
        this.setUploadFlag(ruolo, false);
        this.updateStepAvantiBtn(step, false);
      }
    });
  }

  private updateStepAvantiBtn(step: number, enabled: boolean): void {
    const stepEl = this.$(`[data-step="${step}"]`);
    if (!stepEl) return;
    const btn = stepEl.querySelector('[data-action="step-avanti"]') as HTMLButtonElement | null;
    if (btn) btn.disabled = !enabled;
  }

  private setupActions(): void {
    this.delegate.on('click', '[data-action="step-avanti"]', (e: Event) => {
      e.preventDefault();
      this.goToStep(this.currentStep + 1);
    });
    this.delegate.on('click', '[data-action="step-indietro"]', (e: Event) => {
      e.preventDefault();
      this.goToStep(this.currentStep - 1);
    });
    this.delegate.on('click', '[data-action="avvia"]', (e: Event) => {
      e.preventDefault();
      this.handleAvvia();
    });
    this.delegate.on('click', '[data-action="elimina"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleElimina(id);
    });
    this.delegate.on('click', '[data-action="upload-fenice"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleUploadFenice(id);
    });
    this.delegate.on('click', '[data-action="download-csv"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleDownloadCsv(id);
    });
    this.delegate.on('click', '[data-elab-id]', (e: Event) => {
      if ((e.target as HTMLElement).closest('[data-action]')) return;
      const row = (e.target as HTMLElement).closest('[data-elab-id]') as HTMLElement | null;
      if (row?.dataset.elabId) this.toggleDettaglio(Number(row.dataset.elabId));
    });
  }

  // --- Wizard step ---

  private goToStep(step: number): void {
    if (step < 1 || step > 5) return;

    if (step === 2 && this.currentStep === 1) {
      const anno = this.field('anno');
      const mese = this.field('mese');
      if (!anno || !mese) {
        Weext.toast('Seleziona anno e mese', { type: 'warning' });
        return;
      }
      const cassa = (this.field('cassa') || 'CE') as 'CE' | 'EC';
      this.api.elaborazioni.cleanDir(cassa);
    }

    if (step >= 2 && step <= 4) {
      this.createDropzone(step);
      const config = STEP_CONFIG[step]!;
      if (this.getUploadFlag(config.ruolo)) {
        this.updateStepAvantiBtn(step, true);
      }
    }

    if (step === 2) {
      const cassa = this.field('cassa') || 'CE';
      const label = this.$('[data-cassa-label]');
      if (label) label.textContent = CASSE_LABEL[cassa] ?? cassa;
      const titleEl = this.$('[data-step2-title]');
      const hintEl = this.$('[data-step2-hint]') as HTMLElement | null;
      if (cassa === 'EC') {
        if (titleEl) titleEl.textContent = 'Carica i file zip';
        if (hintEl) {
          hintEl.textContent = 'Seleziona i file zip ricevuti dalla cassa: lavoratori, imprese, iscritti filca. Gli altri file verranno ignorati.';
          hintEl.style.display = '';
        }
      } else {
        if (titleEl) titleEl.textContent = "Carica l'archivio";
        if (hintEl) hintEl.style.display = 'none';
      }
    }

    if (step === 5) {
      this.renderRiepilogo();
    }

    this.showStep(step);
    this.currentStep = step;
  }

  private showStep(step: number): void {
    for (let i = 1; i <= 5; i++) {
      const el = this.$(`[data-step="${i}"]`) as HTMLElement | null;
      if (el) el.style.display = i === step ? '' : 'none';
    }
  }

  private renderRiepilogo(): void {
    const el = this.$('[data-riepilogo]');
    if (!el) return;
    const cassa = this.field('cassa') || 'CE';
    const mese = Number(this.field('mese'));
    const anno = this.field('anno');
    const nomeMese = NOMI_MESE[mese - 1] ?? String(mese);
    const cassaLabel = CASSE_LABEL[cassa] ?? cassa;
    templateParseToElement('elaborazioni-riepilogo', el, {
      cassaLabel,
      nomeMese,
      anno,
      nomeFileArchivio: this.nomeFileArchivio,
      nomeFileFillea: this.nomeFileFillea,
      nomeFileFeneal: this.nomeFileFeneal,
    });
  }

  private idFrom(e: Event): number | null {
    const el = (e.target as HTMLElement).closest('[data-id]') as HTMLElement | null;
    return el?.dataset.id ? Number(el.dataset.id) : null;
  }

  private resetWizard(): void {
    this.uploadArchivio = false;
    this.uploadFillea = false;
    this.uploadFeneal = false;
    this.nomeFileArchivio = '';
    this.nomeFileFillea = '';
    this.nomeFileFeneal = '';
    this.currentStep = 1;
    this.showStep(1);
  }

  // --- Storico ---

  private async caricaStorico(): Promise<void> {
    const result = await this.api.elaborazioni.lista();
    if (!result.ok) {
      const c = this.$('[data-storico]');
      if (c) templateParseToElement('shared-error-state', c, { message: result.error });
      return;
    }
    this.elaborazioni = result.data.elaborazioni;
    this.renderStorico();
    for (const e of this.elaborazioni) {
      if (e.stato === 'IN_CORSO' || e.stato === 'COMPLETATO') this.startPolling(e.id);
    }
  }

  private renderStorico(): void {
    const container = this.$('[data-storico]');
    if (!container) return;
    const data = this.elaborazioni.map(e => ({
      ...e,
      periodo: `${String(e.mese).padStart(2, '0')}/${e.anno}`,
    }));
    templateParseToElement('elaborazioni-storico', container, { elaborazioni: data, feniceEnabled: this.feniceEnabled });
  }

  // --- Avvia ---

  private async handleAvvia(): Promise<void> {
    const cassa = this.field('cassa') || 'CE';
    const anno = this.field('anno');
    const mese = this.field('mese');
    if (!anno || !mese) { Weext.toast('Seleziona anno e mese', { type: 'warning' }); return; }

    const result = await this.api.elaborazioni.crea({ cassa: cassa as 'CE' | 'EC', anno: Number(anno), mese: Number(mese) });
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }

    const { id, cassa: c, anno: a, mese: m } = result.data;
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const nuova: ElaborazioneResponse = {
      id, codice_fenice: 0, cassa: c, anno: a, mese: m, stato: 'IN_CORSO',
      nome_tabella: '', errore: '', file_path: '', importato_at: '',
      created_at: ts, stats: null, warnings: null, importResult: null,
    };
    this.elaborazioni.unshift(nuova);
    this.renderStorico();
    this.startPolling(id);
    this.resetWizard();
  }

  // --- Polling ---

  private startPolling(id: number): void {
    if (this.pollingTimers.has(id)) return;
    this.pollingTimers.set(id, setInterval(() => this.pollElaborazione(id), POLLING_INTERVAL));
  }

  private stopPolling(id: number): void {
    const t = this.pollingTimers.get(id);
    if (t) { clearInterval(t); this.pollingTimers.delete(id); }
  }

  private async pollElaborazione(id: number): Promise<void> {
    const result = await this.api.elaborazioni.dettaglio(id);
    if (!result.ok) return;
    const updated = result.data;
    const idx = this.elaborazioni.findIndex(e => e.id === id);
    if (idx !== -1) this.elaborazioni[idx] = updated;
    const terminale = updated.stato !== 'IN_CORSO' && updated.stato !== 'COMPLETATO';
    if (terminale) { this.stopPolling(id); this.detailCache.set(id, updated); }
    this.renderStorico();
  }

  // --- Elimina ---

  private async handleElimina(id: number): Promise<void> {
    const ok = await Weext.confirm('Eliminare l\'elaborazione? La tabella staging verrà rimossa.', {
      title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger',
    });
    if (!ok) return;
    const result = await this.api.elaborazioni.elimina(id);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }
    Weext.toast('Elaborazione eliminata', { type: 'success' });
    this.elaborazioni = this.elaborazioni.filter(e => e.id !== id);
    this.detailCache.delete(id);
    this.renderStorico();
  }

  // --- Download CSV ---

  private handleDownloadCsv(id: number): void {
    window.open(`/api/elaborazioni/${id}/csv`, '_blank');
  }

  // --- Upload Fenice ---

  private async handleUploadFenice(id: number): Promise<void> {
    if (this.feniceStaging) {
      await Weext.alert(
        'Siamo in modalità staging: una volta a regime, questa funzione caricherà il file su Fenice.',
        { title: 'Funzione non attiva' }
      );
      return;
    }
    const result = await this.api.elaborazioni.uploadFenice(id);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
    } else {
      Weext.toast('Caricato su Fenice', { type: 'success' });
    }
    // Ricarica dettaglio per aggiornare stato Fenice
    const detailResult = await this.api.elaborazioni.dettaglio(id);
    if (detailResult.ok) {
      const idx = this.elaborazioni.findIndex(e => e.id === id);
      if (idx !== -1) this.elaborazioni[idx] = detailResult.data;
      this.detailCache.set(id, detailResult.data);
      this.renderStorico();
      // Re-render dettaglio se aperto
      const contentEl = this.$(`[data-detail-content="${id}"]`);
      const detailRow = this.$(`[data-detail-row="${id}"]`);
      if (contentEl && detailRow && detailRow.style.display !== 'none') {
        templateParseToElement('elaborazioni-dettaglio', contentEl, { elaborazione: detailResult.data, feniceEnabled: this.feniceEnabled });
      }
    }
  }

  // --- Dettaglio inline ---

  private async toggleDettaglio(id: number): Promise<void> {
    const detailRow = this.$(`[data-detail-row="${id}"]`);
    if (!detailRow) return;
    if (detailRow.style.display !== 'none') { detailRow.style.display = 'none'; return; }

    const contentEl = this.$(`[data-detail-content="${id}"]`);
    if (!contentEl) return;

    let elab = this.detailCache.get(id);
    if (!elab) {
      templateParseToElement('shared-loading-state', contentEl, {});
      detailRow.style.display = '';
      const result = await this.api.elaborazioni.dettaglio(id);
      if (!result.ok) {
        templateParseToElement('shared-error-state', contentEl, { message: result.error });
        return;
      }
      elab = result.data;
      this.detailCache.set(id, elab);
    }
    templateParseToElement('elaborazioni-dettaglio', contentEl, { elaborazione: elab, feniceEnabled: this.feniceEnabled });
    detailRow.style.display = '';
  }

  // --- Cleanup ---

  protected override onDisconnected(): void {
    for (const t of this.pollingTimers.values()) clearInterval(t);
    this.pollingTimers.clear();
  }
}
